'use client';

import { useEffect, useRef, useCallback, type RefObject } from 'react';

interface UseInfiniteScrollOptions {
  hasMore: boolean;
  loading: boolean;
  onLoadMore: () => void;
  threshold?: number;
  scrollRef?: RefObject<HTMLDivElement | null>;
}

export function useInfiniteScroll({ hasMore, loading, onLoadMore, threshold = 100, scrollRef: externalScrollRef }: UseInfiniteScrollOptions) {
  const internalScrollRef = useRef<HTMLDivElement | null>(null);
  const scrollElementRef = externalScrollRef || internalScrollRef;

  const checkIfNearBottom = useCallback(() => {
    if (!scrollElementRef.current || loading || !hasMore) return;

    const element = scrollElementRef.current;
    const scrollTop = element.scrollTop;
    const scrollHeight = element.scrollHeight;
    const clientHeight = element.clientHeight;

    if (scrollTop + clientHeight >= scrollHeight - threshold) {
      onLoadMore();
    }
  }, [loading, hasMore, onLoadMore, threshold]);

  const handleScroll = useCallback(
    (event: Event) => {
      if (event.target === scrollElementRef.current) {
        checkIfNearBottom();
      }
    },
    [checkIfNearBottom],
  );

  useEffect(() => {
    const element = scrollElementRef.current;
    if (!element) return;

    element.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      element.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  const checkRef = useRef(checkIfNearBottom);
  useEffect(() => {
    checkRef.current = checkIfNearBottom;
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      checkRef.current();
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  return scrollElementRef;
}
